import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Plus, Search, RefreshCw, X, ChevronLeft,
  CheckCircle, XCircle, Clock, AlertTriangle, Download,
  ArrowLeft, Layers, Calendar, DollarSign, User, FileText,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  getCreditosISR, getCreditoISR, getStatsISR,
  crearCreditoManual, cancelarCredito,
} from '../../../api/creditosISR';
import api from '../../../api/config';
import Pagination from '../../common/Pagination';

const PAGE_SIZE = 15;

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ESTADO_CFG = {
  activo:    { label: 'Activo',    cls: 'bg-green-100 text-green-700',  Icon: CheckCircle },
  agotado:   { label: 'Agotado',   cls: 'bg-gray-100 text-gray-500',    Icon: XCircle     },
  cancelado: { label: 'Cancelado', cls: 'bg-red-100 text-red-600',      Icon: XCircle     },
};

const TIPO_CFG = {
  IR13:   { label: 'IR-13',  cls: 'bg-purple-100 text-purple-700' },
  MANUAL: { label: 'Manual', cls: 'bg-blue-100 text-blue-700'     },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CFG[estado] || ESTADO_CFG.activo;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <cfg.Icon size={10} /> {cfg.label}
    </span>
  );
}

function TipoBadge({ tipo }) {
  const cfg = TIPO_CFG[tipo] || TIPO_CFG.MANUAL;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ── KPI card ─────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, color = 'gray', icon: Icon }) {
  const colors = {
    gray:   'border-gray-200 bg-white',
    green:  'border-green-200 bg-green-50',
    blue:   'border-blue-200 bg-blue-50',
    purple: 'border-purple-200 bg-purple-50',
    amber:  'border-amber-200 bg-amber-50',
  };
  const textColors = {
    gray: 'text-gray-900', green: 'text-green-700', blue: 'text-blue-700',
    purple: 'text-purple-700', amber: 'text-amber-700',
  };
  return (
    <div className={`border rounded-xl p-4 ${colors[color]}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
          <p className={`text-xl font-bold mt-1 ${textColors[color]}`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg bg-white bg-opacity-60`}>
            <Icon size={16} className={textColors[color]} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal crear crédito manual ────────────────────────────────────────────────
function CrearCreditoModal({ companyId, onClose, onSuccess }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employeeId: '', monto: '', anio: new Date().getFullYear(), descripcion: '', notas: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/employees', { params: { companyId, estado: 'activo', limit: 500 } })
      .then(r => setEmployees(r.data?.employees || r.data || []))
      .catch(() => {});
  }, [companyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employeeId || !form.monto || !form.anio) {
      setError('Empleado, monto y año son obligatorios.');
      return;
    }
    if (parseFloat(form.monto) <= 0) {
      setError('El monto debe ser mayor a 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await crearCreditoManual({ companyId, ...form });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear el crédito');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 rounded-lg"><CreditCard size={15} className="text-blue-600" /></div>
            <h3 className="text-sm font-bold text-gray-900">Nuevo Crédito ISR Manual</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Empleado *</label>
            <select
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.employeeId}
              onChange={e => set('employeeId', e.target.value)}
            >
              <option value="">Seleccionar empleado…</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.apellido}, {emp.nombre} — {emp.employee_id}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Monto (RD$) *</label>
              <input
                type="number" min="0.01" step="0.01"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
                value={form.monto}
                onChange={e => set('monto', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Año Fiscal *</label>
              <input
                type="number" min="2020" max="2099"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.anio}
                onChange={e => set('anio', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descripción</label>
            <input
              type="text" maxLength={200}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ej: Corrección ISR período 2024"
              value={form.descripcion}
              onChange={e => set('descripcion', e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notas internas</label>
            <textarea
              rows={2} maxLength={500}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Observaciones adicionales…"
              value={form.notas}
              onChange={e => set('notas', e.target.value)}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50">
              {saving ? 'Guardando…' : 'Crear Crédito'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detalle de crédito ────────────────────────────────────────────────────────
function DetalleCredito({ creditoId, companyId, onBack, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getCreditoISR(creditoId, companyId)
      .then(setData)
      .catch(() => setError('Error al cargar el crédito'))
      .finally(() => setLoading(false));
  }, [creditoId, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCancelar = async () => {
    if (!motivo.trim()) return;
    setCanceling(true);
    try {
      await cancelarCredito(creditoId, companyId, motivo);
      onRefresh();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cancelar');
    } finally {
      setCanceling(false);
      setShowCancel(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const { credito, aplicaciones } = data;
  const pctUsado = credito.monto_original > 0
    ? ((credito.monto_original - credito.saldo) / credito.monto_original * 100).toFixed(0)
    : 0;

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <ArrowLeft size={14} /> Volver
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold text-gray-900">{credito.empleado_nombre}</h2>
            <TipoBadge tipo={credito.tipo} />
            <EstadoBadge estado={credito.estado} />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {credito.empleado_codigo} · {credito.departamento || 'Sin departamento'} · Año {credito.anio}
          </p>
        </div>
        {credito.estado === 'activo' && (
          <button onClick={() => setShowCancel(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors">
            <X size={13} /> Cancelar crédito
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Monto Original" value={`RD$ ${fmt(credito.monto_original)}`} color="blue" icon={DollarSign} />
        <KPICard label="Saldo Disponible" value={`RD$ ${fmt(credito.saldo)}`}
          color={credito.estado === 'activo' ? 'green' : 'gray'} icon={CreditCard} />
        <KPICard label="Total Aplicado"
          value={`RD$ ${fmt(credito.monto_original - credito.saldo)}`}
          sub={`${pctUsado}% utilizado`} color="purple" icon={Layers} />
        <KPICard label="Fecha Creación" value={fmtDate(credito.created_at)} icon={Calendar} />
      </div>

      {/* Barra de progreso */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-600">Utilización del crédito</p>
          <p className="text-xs text-gray-400">{pctUsado}% aplicado</p>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${credito.estado === 'agotado' ? 'bg-gray-400' : 'bg-blue-500'}`}
            style={{ width: `${pctUsado}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <p className="text-xs text-gray-400">RD$ 0</p>
          <p className="text-xs text-gray-400">RD$ {fmt(credito.monto_original)}</p>
        </div>
      </div>

      {/* Descripción / notas */}
      {(credito.descripcion || credito.notas) && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
          {credito.descripcion && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Descripción</p>
              <p className="text-sm text-gray-700">{credito.descripcion}</p>
            </div>
          )}
          {credito.notas && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Notas</p>
              <p className="text-xs text-gray-500 whitespace-pre-line">{credito.notas}</p>
            </div>
          )}
        </div>
      )}

      {/* Historial de aplicaciones */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">Historial de Aplicaciones</p>
          <span className="text-xs text-gray-400">{aplicaciones.length} aplicación{aplicaciones.length !== 1 ? 'es' : ''}</span>
        </div>

        {aplicaciones.length === 0 ? (
          <div className="py-10 text-center text-xs text-gray-400">
            <Clock size={24} className="mx-auto mb-2 opacity-30" />
            Este crédito aún no ha sido aplicado en ninguna nómina
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Período</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">ISR Calculado</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Crédito Aplicado</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">ISR Final</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {aplicaciones.map(a => (
                <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-gray-800">{a.periodo}</p>
                    <p className="text-xs text-gray-400">{a.payroll_tipo}{a.quincena ? ` · ${a.quincena}` : ''}</p>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-600">RD$ {fmt(a.isr_calculado)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-green-600">
                    − RD$ {fmt(a.monto_aplicado)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-gray-900">RD$ {fmt(a.isr_final)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-400 text-xs">{fmtDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal cancelar */}
      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-500" />
              <h3 className="text-sm font-bold text-gray-900">Cancelar crédito</h3>
            </div>
            <p className="text-xs text-gray-500">
              El crédito quedará cancelado y su saldo disponible ({fmt(credito.saldo)} RD$) ya no se aplicará en futuras nóminas.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Motivo *</label>
              <textarea rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                placeholder="Indique el motivo de la cancelación…"
                value={motivo} onChange={e => setMotivo(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCancel(false)}
                className="flex-1 px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleCancelar} disabled={!motivo.trim() || canceling}
                className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50">
                {canceling ? 'Cancelando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Módulo principal ─────────────────────────────────────────────────────────
export default function CreditosISRModule({ currentCompany }) {
  const companyId = currentCompany?.id;

  const [creditos, setCreditos] = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo]     = useState('');
  const [showModal, setShowModal]       = useState(false);
  const [selectedId, setSelectedId]     = useState(null);
  const [page, setPage]                 = useState(1);
  const [showStats, setShowStats]       = useState(true);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError('');

    const params = {};
    if (filtroEstado) params.estado = filtroEstado;
    if (filtroTipo)   params.tipo   = filtroTipo;

    Promise.all([
      getCreditosISR(companyId, params),
      getStatsISR(companyId),
    ])
      .then(([list, s]) => { setCreditos(list); setStats(s); })
      .catch(() => setError('Error al cargar los créditos ISR'))
      .finally(() => setLoading(false));
  }, [companyId, filtroEstado, filtroTipo]);

  useEffect(() => { load(); }, [load]);

  const filtered = creditos.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.empleado_nombre?.toLowerCase().includes(q) ||
      c.empleado_codigo?.toLowerCase().includes(q) ||
      c.descripcion?.toLowerCase().includes(q)
    );
  });

  useEffect(() => { setPage(1); }, [search, filtroEstado, filtroTipo]);

  const totalPagesCreditos = Math.ceil(filtered.length / PAGE_SIZE);
  const pagedFiltered = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (selectedId) {
    return (
      <DetalleCredito
        creditoId={selectedId}
        companyId={companyId}
        onBack={() => setSelectedId(null)}
        onRefresh={load}
      />
    );
  }

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 rounded-xl border border-blue-100">
            <CreditCard size={16} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-base font-bold text-gray-900">Créditos ISR</h1>
            <p className="text-xs text-gray-400">Saldos a favor aplicables en nómina (FIFO)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
            <RefreshCw size={12} /> Actualizar
          </button>
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors">
            <Plus size={12} /> Nuevo Crédito Manual
          </button>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div>
          <button
            onClick={() => setShowStats(s => !s)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mb-2 transition-colors"
          >
            {showStats ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showStats ? 'Ocultar resumen' : 'Ver resumen'}
          </button>
          {showStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPICard label="Total Disponible"
                value={`RD$ ${fmt(stats.totals?.total_disponible)}`}
                sub={`${stats.totals?.activos || 0} crédito(s) activo(s)`}
                color="green" icon={CreditCard} />
              <KPICard label="Total Aplicado"
                value={`RD$ ${fmt(stats.totals?.total_aplicado)}`}
                color="purple" icon={CheckCircle} />
              <KPICard label="Créditos IR-13"
                value={stats.by_tipo?.find(t => t.tipo === 'IR13')?.cantidad || 0}
                sub={`RD$ ${fmt(stats.by_tipo?.find(t => t.tipo === 'IR13')?.saldo_disponible)} disponible`}
                color="blue" icon={FileText} />
              <KPICard label="Créditos Manuales"
                value={stats.by_tipo?.find(t => t.tipo === 'MANUAL')?.cantidad || 0}
                sub={`RD$ ${fmt(stats.by_tipo?.find(t => t.tipo === 'MANUAL')?.saldo_disponible)} disponible`}
                color="amber" icon={User} />
            </div>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Buscar por empleado…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="agotado">Agotado</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <select
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
        >
          <option value="">Todos los tipos</option>
          <option value="IR13">IR-13</option>
          <option value="MANUAL">Manual</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <CreditCard size={32} className="mx-auto mb-3 text-gray-200" />
            <p className="text-sm font-medium text-gray-400">No hay créditos ISR</p>
            <p className="text-xs text-gray-300 mt-1">
              {filtroEstado || filtroTipo || search
                ? 'Ajusta los filtros para ver más resultados'
                : 'Los créditos se generan al cerrar el IR-13 o de forma manual'}
            </p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Empleado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Tipo</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Año</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">Monto Original</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">Saldo</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">Aplicado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pagedFiltered.map(c => {
                  const aplicado = parseFloat(c.monto_original) - parseFloat(c.saldo);
                  const pct = c.monto_original > 0 ? (aplicado / c.monto_original * 100) : 0;
                  return (
                    <tr key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{c.empleado_nombre}</p>
                        <p className="text-xs text-gray-400">{c.empleado_codigo} · {c.departamento || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-center"><TipoBadge tipo={c.tipo} /></td>
                      <td className="px-4 py-3 text-center text-gray-600 font-mono">{c.anio}</td>
                      <td className="px-4 py-3 text-right text-gray-700">RD$ {fmt(c.monto_original)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">RD$ {fmt(c.saldo)}</td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-gray-600">RD$ {fmt(aplicado)}</p>
                        {pct > 0 && (
                          <div className="w-full bg-gray-100 rounded-full h-1 mt-1">
                            <div className="bg-blue-400 h-1 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center"><EstadoBadge estado={c.estado} /></td>
                      <td className="px-4 py-3 text-right text-xs text-gray-400">{fmtDate(c.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              page={page}
              totalPages={totalPagesCreditos}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <CrearCreditoModal
          companyId={companyId}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
